<template>
    <div class="card py-5 text-center">
        <div class="card-header">
            <h4 class="card-title">
                {{ $t('users.users_msg__batch_delete_title') }}
            </h4>
            <p class="lead">
                {{ $t('users.users_msg__batch_delete_greeting_msg') }}
            </p>
        </div>

        <div id="formUpload" class="card-body me-3 ms-3">
            <div
                class="d-flex align-items-center justify-content-start flex-wrap"
            >
                <input
                    type="file"
                    accept=".xlsx"
                    id="xlsxFile"
                    name="xlsxFile"
                    ref="fileInput"
                    style="display: none"
                    @change="handleFileSelection"
                />

                <button
                    class="btn btn-outline-primary mx-2 button-custom"
                    id="btnChooseFile"
                    @click="triggerFileInput"
                >
                    {{ $t('users.users_btn__choose_file') }}
                </button>

                <button
                    class="btn btn-danger ms-auto"
                    id="btnUpload"
                    :disabled="!selectedFile"
                    @click="uploadFile"
                >
                    {{ $t('users.users_btn__batch_delete') }}
                </button>
            </div>
        </div>

        <div class="d-flex align-items-center justify-content-center flex-wrap">
            <div class="col-md-12">
                <div id="divInvalidResult" v-if="invalidResults.length > 0">
                    <hr />
                    <div class="alert alert-danger" id="invalidUserAlert">
                        {{
                            $t(
                                'users.users_msg__batch_delete_invalid_user_alert'
                            )
                        }}
                    </div>
                    <h2>
                        {{
                            $t(
                                'users.users_msg__batch_delete_invalid_table_title'
                            )
                        }}
                    </h2>
                    <div class="table-responsive batch-result-table">
                        <table
                            class="table table-striped w-100"
                            id="validationErrorTable"
                        >
                            <thead>
                                <tr>
                                    <th
                                        v-for="column in invalidTableColumns"
                                        :key="column.field"
                                    >
                                        {{ getColumnLabel(column) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(error, index) in invalidResults"
                                    :key="index"
                                >
                                    <td
                                        v-for="column in invalidTableColumns"
                                        :key="column.field"
                                    >
                                        {{ getCellValue(error, column.field) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <hr />
                </div>

                <div id="divValidResult" v-if="validResults.length > 0">
                    <h2>
                        {{
                            $t(
                                'users.users_msg__batch_delete_valid_table_title'
                            )
                        }}
                    </h2>
                    <div class="table-responsive batch-result-table">
                        <table
                            class="table table-striped w-100"
                            id="correctTable"
                        >
                            <thead>
                                <tr>
                                    <th
                                        v-for="column in tableColumns"
                                        :key="column.field"
                                    >
                                        {{ getColumnLabel(column) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(result, index) in validResults"
                                    :key="index"
                                >
                                    <td
                                        v-for="column in tableColumns"
                                        :key="column.field"
                                    >
                                        {{ getCellValue(result, column.field) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <hr />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, reactive, inject, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    batchDeleteUsers,
    checkBatchDeleteUsersPermission,
    fetchBatchDeleteConfig,
} from '@/api/users';

export default {
    name: 'UserBatchDelete',
    setup() {
        const fileInput = ref(null);
        const selectedFile = ref(null);
        const validResults = reactive([]);
        const invalidResults = reactive([]);
        const batchDeleteConfig = ref([]);

        const { t } = useI18n();
        const Swal = inject('$swal');

        const triggerFileInput = () => {
            fileInput.value.click();
        };

        const tableColumns = computed(() => {
            if (batchDeleteConfig.value.length > 0) {
                return batchDeleteConfig.value.map(config => ({
                    field: config.field,
                    field_name: config.field_name,
                }));
            }
            // Fallback: derive columns from the first valid/invalid result
            const sampleRow = validResults[0] || invalidResults[0];
            if (sampleRow) {
                return Object.keys(sampleRow)
                    .filter(key => key !== 'error_detail_message')
                    .map(key => ({
                        field: key,
                        field_name: null,
                    }));
            }
            return [];
        });

        const invalidTableColumns = computed(() => {
            const columns = [...tableColumns.value];
            columns.push({
                field: 'error_detail_message',
                field_name: 'users_fld__error_detail_message',
            });
            return columns;
        });

        const getColumnLabel = column => {
            if (column.field_name) {
                return t(`users.${column.field_name}`);
            }
            // Fallback: format field name as readable label
            return column.field
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
        };

        const getCellValue = (row, field) => {
            if (field === 'error_detail_message') {
                return row?.error_detail_message ?? '';
            }
            return row?.[field] ?? '';
        };

        const handleFileSelection = async event => {
            const file = event.target.files[0];
            if (!file) return;

            const isExcelMimeType =
                file.type ===
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const isExcelExtension = file.name?.toLowerCase().endsWith('.xlsx');

            if (isExcelMimeType || isExcelExtension) {
                selectedFile.value = file;

                const formData = new FormData();
                formData.append('file', selectedFile.value);
                formData.append('is_check', true);

                try {
                    const response = await batchDeleteUsers(formData);
                    validResults.splice(0, validResults.length);
                    invalidResults.splice(0, invalidResults.length);

                    if (response.data.errors.length > 0) {
                        selectedFile.value = null;
                        response.data.errors.forEach(result => {
                            invalidResults.push(result);
                        });
                    }

                    if (response.data.valids.length > 0) {
                        response.data.valids.forEach(result => {
                            validResults.push(result);
                        });
                    }
                } catch (error) {
                    await Swal.fire({
                        title: t('users.users_msg__validation_failed'),
                        text: t(
                            'users.users_msg__validation_failed_some_thing_went_wrong'
                        ),
                        icon: 'error',
                    });
                    selectedFile.value = null;
                }
            } else {
                await Swal.fire({
                    title: t('users.users_msg__invalid_file'),
                    text: t('users.users_msg__invalid_file_msg'),
                    icon: 'error',
                });
                selectedFile.value = null;
            }
        };

        const uploadFile = async () => {
            if (!selectedFile.value) return;

            const formData = new FormData();
            formData.append('file', selectedFile.value);
            formData.append('is_check', false);

            try {
                const response = await batchDeleteUsers(formData);
                if (
                    response.data.success_count > 0 &&
                    response.data.errors.length === 0
                ) {
                    await Swal.fire({
                        title: t('users.users_msg__batch_delete_success'),
                        text: t('users.users_msg__batch_delete_success_msg'),
                        icon: 'success',
                    });
                } else {
                    invalidResults.splice(0, invalidResults.length);
                    if (response.data.errors.length > 0) {
                        response.data.errors.forEach(result => {
                            invalidResults.push(result);
                        });
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                selectedFile.value = null;
            }
        };

        onMounted(async () => {
            try {
                await checkBatchDeleteUsersPermission();
                batchDeleteConfig.value = await fetchBatchDeleteConfig();
            } catch (error) {
                console.error(error);
            }
        });

        return {
            fileInput,
            selectedFile,
            validResults,
            invalidResults,
            tableColumns,
            invalidTableColumns,
            getColumnLabel,
            getCellValue,
            triggerFileInput,
            handleFileSelection,
            uploadFile,
        };
    },
};
</script>

<style scoped>
.batch-result-table {
    max-height: 420px;
    overflow: auto;
}

.batch-result-table :deep(th),
.batch-result-table :deep(td) {
    white-space: nowrap;
}
</style>
